<template>
  <v-row>
    <v-col cols="12" class="text-center">
      <h2 class="mt-6 mb-6">{{ t('Explora_todas_las_actividades') }}
        <v-fab v-if="profesionalesDisponibles.length > 1" absolute location="top right" icon @click="openFiltratge">
          <v-icon>{{ profesionalesSeleccionados.length > 0 ? 'mdi-filter' : 'mdi-filter-off' }}</v-icon>
        </v-fab>
      </h2>
      <v-row>
        <template v-if="loading"><v-col v-for="k in 3" :key="k" cols="12" sm="6" md="4">
            <v-skeleton-loader :elevation="1" type="card"></v-skeleton-loader>
          </v-col>
        </template>
        <v-col v-for="curso in cursosToShow" :key="curso.id" cols="12" sm="6" md="4">
          <v-card class="h-100 d-flex flex-column">
            <v-img height="200" cover :src="curso.imagen_url"></v-img>
            <v-card-title>{{ curso.titulo }}</v-card-title>
            <v-card-text>{{ curso.descripcion }}</v-card-text>
            <v-spacer></v-spacer>
            <v-card-actions>
              <v-btn color="primary" :to="`/curso/${curso.id}`" text>{{
                t('Acceder') }}</v-btn>
              <template v-if="curso.ranking && curso.ranking.length > 0">
                <v-spacer></v-spacer>
                <RankingCurso :ranking="curso.ranking"/>
              </template>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <!-- Nota y botón para mostrar todos si se está filtrando y existen resultados filtrados -->
      <v-row v-if="profesionalesSeleccionados.length > 0 && profesionalesDisponibles.length > 1">
        <v-col cols="12" class="text-center">
          <p>{{ t('Actividades_filtradas_según_especialidad') }}</p>
          <v-btn class="mt-4" color="primary" @click="clearSelection">
            {{ t('Ver_mas_especialidades') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
  <!-- Diálogo para Seleccionar especialidades -->
  <v-dialog v-model="open" max-width="600px">
    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>{{ t('Filtrar_por_especialidad') }}</v-toolbar-title>
        <v-btn color="primary" text prepend-icon="mdi-filter" @click="applyFilter">{{ t('Aplicar') }}</v-btn>
        <v-btn icon @click="open = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-select multiple return-object clearable v-model="profesionalesSeleccionados"
          :items="profesionalesDisponibles" item-title="categoria" item-value="id" :label="t('Especialidad')">
        </v-select>
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- Diálogo para Solicitar Acceso -->
  <v-dialog v-model="dialog" max-width="600px" height="600">
    <v-card>
      <v-card-title class="text-h5">{{ t('Quieres_acceder_a_la_actividad') }} {{ cursoSeleccionado.titulo
      }}?</v-card-title>
      <v-card-text>
        <span v-if="mensajeExperiencia">{{ mensajeExperiencia }}<br><br></span>
        {{ t('Para_acceder_a_la_actividad') }}</v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="dialog = false">{{ t('Cerrar') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>


<script setup>
import { ref, computed, watch } from 'vue'
import { useUserStore }   from '@/stores/user';
import { useCursosStore } from '@/stores/cursos';
import { useI18n } from 'vue-i18n'
import RankingCurso from './RankingCurso.vue';

const userStore   = useUserStore();
const cursosStore = useCursosStore();

// Access courses and loading flag from the store state
const loading      = computed(() => cursosStore.loadingCursos);
const loadingprof  = computed(() => userStore.loadingProfesionales);
const loadinguserDetails = computed(() => userStore.loadingUserDetails); 

// Get translation function
const { t } = useI18n()

// Local reactive state
const dialog = ref(false)
const cursoSeleccionado = ref({})
const mensajeExperiencia = ref('')

// Obtenemos el id profesional del usuario (si userDetails está cargado)
const userProfesional = computed(() => userStore.userDetails?.profesional ?? null);

// Variable reactiva para la selección de profesionales (objetos completos)
const profesionalesSeleccionados = ref([])
const open = ref(false)

// Computed que devuelve solo las especialidades que tengan al menos un curso asociado.
// Si los profesionales aún no están cargados, devuelve un array vacío.
const profesionalesDisponibles = computed(() => {
  if (loadingprof.value) return [];
  return userStore.profesionales.filter(prof =>
    cursosStore.cursos.some(
      curso => curso.profesionales?.includes(prof.id)
    )
  );
});

// Watcher para preseleccionar la especialidad del usuario cuando los profesionales ya estén cargados
watch(
  [userProfesional, loadingprof, profesionalesDisponibles, loadinguserDetails],
  ([nuevoValor, loadingProf, disponibles, loadingUser]) => {
    if (!loadingUser && !loadingProf && nuevoValor) {
      const profSeleccionado = disponibles.find(p => p.id === nuevoValor)
      if (profSeleccionado) {
        profesionalesSeleccionados.value = [profSeleccionado]
      }
    }
  },
  { immediate: true }
)

// Computed para obtener un array de IDs a partir de los objetos seleccionados
const selectedProfesionalIds = computed(() =>
  profesionalesSeleccionados.value.map(p => p.id)
)

// Filtrado de cursos: se muestran solo aquellos que tengan algún id presente en selectedProfesionalIds
const cursosFiltrados = computed(() => {
  if (!selectedProfesionalIds.value.length) return [];
  return cursosStore.cursos.filter(c =>
    c.profesionales?.some(id => selectedProfesionalIds.value.includes(id))
  );
});

// Determina qué listado mostrar: si hay filtro y cursos filtrados se muestran estos, sino se muestran todos los cursos
const cursosToShow = computed(() =>
  selectedProfesionalIds.value.length && cursosFiltrados.value.length
    ? cursosFiltrados.value
    : cursosStore.cursos
);

function openFiltratge() {
  open.value = true
}

function applyFilter() {
  open.value = false; // o la lógica que cierre el diálogo y aplique el filtro
}

function clearSelection() {
  profesionalesSeleccionados.value = []; // Resetea la selección
  open.value = false;
}


// Function to set the selected course and build the experience message
/*
function solicitarAcceso(cursoId) {
  cursoSeleccionado.value = cursos.value.find(curso => curso.id === cursoId)

  if (cursoSeleccionado.value) {
    if (
      cursoSeleccionado.value.experiencia_minima === 0 &&
      cursoSeleccionado.value.experiencia_maxima
    ) {
      mensajeExperiencia.value = t('mensaje_para_residentes_con_menos_experiencia', {
        experiencia_maxima: cursoSeleccionado.value.experiencia_maxima
      })
    } else if (
      cursoSeleccionado.value.experiencia_minima &&
      !cursoSeleccionado.value.experiencia_maxima
    ) {
      mensajeExperiencia.value = t('mensaje_para_residentes_con_al_menos_experiencia', {
        experiencia_minima: cursoSeleccionado.value.experiencia_minima
      })
    } else if (
      cursoSeleccionado.value.experiencia_minima &&
      cursoSeleccionado.value.experiencia_maxima
    ) {
      mensajeExperiencia.value = t('mensaje_para_residentes_con_rango_experiencia', {
        experiencia_minima: cursoSeleccionado.value.experiencia_minima,
        experiencia_maxima: cursoSeleccionado.value.experiencia_maxima
      })
    } else {
      mensajeExperiencia.value = ''
    }
    dialog.value = true
  }
}*/
</script>
